import { z } from 'zod';
import type { BridgeMode, BridgeTimingMode, DraftQuestion } from './drafts.js';
import type { TemplateKey } from './templates.ts';

export const publicationDurationKeys = ['1h', '4h', '1d', '2d', '7d'] as const;
export type PublicationDurationKey = (typeof publicationDurationKeys)[number];

export const publicationDurationMilliseconds: Record<PublicationDurationKey, number> = {
  '1h': 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
  '2d': 2 * 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

export const publicationDurationLabels: Record<PublicationDurationKey, string> = {
  '1h': '1 saat',
  '4h': '4 saat',
  '1d': '1 gün',
  '2d': '2 gün',
  '7d': '7 gün',
};

export const publishDraftInputSchema = z.object({
  duration: z.enum(publicationDurationKeys).default('2d'),
});

export type PublicationSummary = {
  revision?: number;
  id: string;
  draftId: string | null;
  title: string;
  templateKey: TemplateKey;
  slug: string;
  url: string;
  duration: PublicationDurationKey;
  publishedAt: number;
  expiresAt: number;
};

export type CreatorResourceUsage = {
  drafts: number;
  activePublications: number;
  draftMediaBytes: number;
  publicationMediaBytes: number;
  totalMediaBytes: number;
  limits: {
    drafts: number;
    activePublications: number;
    mediaBytes: number;
  };
  mediaUploads: {
    allowed: boolean;
    reason: 'USER_MEDIA_QUOTA_EXCEEDED' | 'PLATFORM_MEDIA_QUOTA_EXCEEDED' | 'DISK_RESERVE_REACHED' | null;
    message: string | null;
  };
};

export type PlatformStorageUsage = {
  activePublications: number;
  expiredPublicationsAwaitingCleanup: number;
  draftMediaBytes: number;
  publicationMediaBytes: number;
  totalMediaBytes: number;
  limitBytes: number;
};

export type PublishedGame = {
  revision?: number;
  id: string;
  title: string;
  templateKey: TemplateKey;
  maxLevels: number;
  trackTime: boolean;
  bridgeMode?: BridgeMode;
  timingMode?: BridgeTimingMode;
  repeatUntilFall?: boolean;
  questions: DraftQuestion[];
  publishedAt: number;
  expiresAt: number;
};
